import React, { useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-toastify";
import "../../../style/slotbooking.css";
import {
  SLOT_MAX_DAYS_AHEAD,
  SlotAvailability,
  DynamicSchedule,
  GeneratedSlot,
  addDaysYMD,
  generateSlots,
  formatSlotDateLong,
  formatSlotTime,
  getTodayYMD,
  isTimePassedToday,
  parseYMD,
  timeToMinutes,
  toYMD,
} from "../../../utils/slotBooking";

interface SlotScheduleProps {
  slotDate: string;
  schedule: DynamicSchedule | null;
bookedSlots?: Record<string, number>;
  slotTime: string; // window start, e.g. "13:00"
  onDateChange: (date: string) => void;
  onTimeChange: (time: string) => void;
  onContinue: () => void;
  // optional: backend se aane wali availability (time -> status). Na ho to sab Available.
  availability?: Record<string, SlotAvailability>;
  continueLabel?: string;
}

const AVAIL_LABEL: Record<SlotAvailability, string> = {
  available: "Available",
  almost: "Almost full",
  full: "Full",
};
const groupGeneratedSlots = (slots: GeneratedSlot[]) => {
  const hour = (time: string) => Number(time.split(":")[0]);

  return [
    {
      label: "Morning",
      icon: "bi-sunrise",
      slots: slots.filter((s) => hour(s.startTime) < 12),
    },
    {
      label: "Afternoon",
      icon: "bi-sun",
      slots: slots.filter(
        (s) => hour(s.startTime) >= 12 && hour(s.startTime) < 16
      ),
    },
    {
      label: "Evening",
      icon: "bi-moon-stars",
      slots: slots.filter((s) => hour(s.startTime) >= 16),
    },
  ].filter((group) => group.slots.length > 0);
};
const SlotSchedule: React.FC<SlotScheduleProps> = ({
  slotDate,
  slotTime,
  onDateChange,
  onTimeChange,
  onContinue,
  availability = {},
  bookedSlots = {},
  schedule,
  continueLabel = "Continue",
}) => {
  const today = getTodayYMD();
  const tomorrow = addDaysYMD(1);
const slots = useMemo(
  () => generateSlots(schedule),
  [schedule]
);

const groups = useMemo(
  () => groupGeneratedSlots(slots),
  [slots]
);

const selectedSlot = slots.find(
  (s) => s.startTime === slotTime
);
  const isToday = slotDate === today;
  const minDate = parseYMD(today)!;
  const maxDate = parseYMD(addDaysYMD(SLOT_MAX_DAYS_AHEAD))!;

  const pickDate = (value: string) => {
    onDateChange(value);
    if (value === today && slotTime && isTimePassedToday(slotTime)) {
      onTimeChange("");
    }
  };
const getBookingStartTime = (time: string) => {
  const totalMinutes = timeToMinutes(time) - 60;
  const normalized = (totalMinutes + 1440) % 1440;

  return `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(
    normalized % 60
  ).padStart(2, "0")}`;
};
  const handleContinue = () => {
    if (!slotDate) {
      toast.info("Please select a date");
      return;
    }
    if (!slotTime) {
      toast.info("Please select a delivery slot");
      return;
    }
    onContinue();
  };

  const ready = !!slotDate && !!slotTime;

  return (
    <div className="slot-card">
      <div className="slot-card-head">
        <div className="slot-head-icon">
          <i className="bi bi-truck"></i>
        </div>
        <div>
          <h5 className="slot-card-title">Schedule your order</h5>
          <p className="slot-card-sub">
            Pick a date and start time. Every slot is a 90-minute express order with a clear delivery commitment.
          </p>
        </div>
      </div>

      <div className="row g-4">
        {/* ---------------- DATE ---------------- */}
        <div className="col-lg-5">
          <div className="slot-section-label">
            <span className="slot-step-dot">1</span> Select Date
          </div>

          <div className="slot-quick-row">
            <button
              type="button"
              className={`slot-chip ${slotDate === today ? "active" : ""}`}
              onClick={() => pickDate(today)}
            >
              Today
            </button>
            <button
              type="button"
              className={`slot-chip ${slotDate === tomorrow ? "active" : ""}`}
              onClick={() => pickDate(tomorrow)}
            >
              Tomorrow
            </button>
          </div>

          <div className="slot-calendar">
            <DatePicker
              inline
              selected={parseYMD(slotDate)}
              onChange={(date: Date | null) => date && pickDate(toYMD(date))}
              minDate={minDate}
              maxDate={maxDate}
              calendarStartDay={1}
            />
          </div>
        </div>

        {/* ---------------- DELIVERY SLOT ---------------- */}
        <div className="col-lg-7">
          <div className="slot-section-label">
            <span className="slot-step-dot">2</span> Select Time
          </div>

          {!slotDate ? (
            <div className="slot-empty">
              <i className="bi bi-clock-history"></i>
              <p>Select a date to see available slots</p>
            </div>
          ) : (
            <>
          {groups.map((group) => (
  <div className="slot-group" key={group.label}>
    <div className="slot-group-title">
      <i className={`bi ${group.icon}`}></i> {group.label}
    </div>

    <div className="slot-window-grid">
      {group.slots.map((slot) => {
        const t = slot.startTime;
        const count = bookedSlots[t] ?? 0;

        const capacityStatus: SlotAvailability =
          count >= (schedule?.maxCapacity ?? 1)
            ? "full"
            : count >= Math.max(1, (schedule?.maxCapacity ?? 1) - 1)
              ? "almost"
              : "available";

        const avail = availability[t] ?? capacityStatus;

        const passed = isToday && isTimePassedToday(t);
        const disabled = passed || avail === "full";

        return (
          <button
            key={t}
            type="button"
            disabled={disabled}
            className={`slot-window ${slotTime === t ? "active" : ""}`}
            onClick={() => onTimeChange(t)}
          >
            <span className="sw-time">
              {formatSlotTime(t)}
            </span>

            <span className="sw-sub">
              Delivery by {formatSlotTime(slot.endTime)}
            </span>

            <span className={`sw-status ${avail}`}>
              <i className="bi bi-circle-fill"></i>{" "}
              {passed ? "Passed" : AVAIL_LABEL[avail]}
            </span>
          </button>
        );
      })}
    </div>
  </div>
))}

              <div className="slot-legend">
                <span className="available"><i className="bi bi-circle-fill"></i> Available</span>
                <span className="almost"><i className="bi bi-circle-fill"></i> Almost full</span>
                <span className="full"><i className="bi bi-circle-fill"></i> Full</span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ---------------- SELECTED SLOT (prominent) ---------------- */}
      <div className={`slot-selected ${ready ? "ready" : ""}`}>
        {ready ? (
          <>
            <div className="slot-selected-flow vertical">
              <b>{formatSlotTime(slotTime)}</b>
              <i className="bi bi-arrow-down"></i>
             <span className="flow-90">
  {schedule?.durationMinutes ?? 0} MINUTES
</span>
              <i className="bi bi-arrow-down"></i>
              <b>{formatSlotTime(selectedSlot?.endTime)}</b>
            </div>
            <div className="slot-selected-meta">
              <div>
                <small>Scheduled Start</small>
                <strong>{formatSlotDateLong(slotDate)}, {formatSlotTime(slotTime)}</strong>
              </div>
              <div>
                <small>Expected Delivery</small>
              <strong>
  {formatSlotDateLong(slotDate)},{" "}
  {formatSlotTime(selectedSlot?.endTime)}
</strong>
              </div>
              <div className="full-row">
                <strong className="express-note">
                  <i className="bi bi-check-circle-fill"></i> 90-minute express processing
                </strong>
                <small>
                  {isToday
                    ? "Scan upload opens at your slot start time"
                    : "Scan upload opens when your scheduled slot starts"}
                </small>
              </div>
            </div>
          </>
        ) : (
          <span className="slot-summary-placeholder">No slot selected yet</span>
        )}

        <button
          className="slot-continue-btn"
          disabled={!ready}
          onClick={handleContinue}
        >
          {continueLabel} <i className="bi bi-arrow-right"></i>
        </button>
      </div>
    </div>
  );
};

export default SlotSchedule;
